// src/pages/buyer/DesignAssistantPage.jsx
import { useState } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { designAssistantApi } from '@/api/services'
import { Link } from 'react-router-dom'
import { Wand2, Loader2, Palette, Lightbulb, ShieldCheck, Clock, Sparkles } from 'lucide-react'
import toast from 'react-hot-toast'

const GARMENT_TYPES = ['Saree', 'Kurta', 'Lehenga', 'Dress', 'Shirt', 'Blouse', 'Dupatta', 'Salwar', 'Jacket', 'Sherwani']
const OCCASIONS = ['Wedding', 'Casual', 'Formal', 'Festive', 'Party', 'Office', 'Ethnic', 'Resort']
const SEASONS = ['Summer', 'Winter', 'Monsoon', 'Spring']
const BUDGETS = [
  { value: 'under_500', label: 'Under ₹500/m' },
  { value: '500_1000', label: '₹500 – ₹1,000/m' },
  { value: '1000_2500', label: '₹1,000 – ₹2,500/m' },
  { value: '2500_5000', label: '₹2,500 – ₹5,000/m' },
  { value: 'above_5000', label: '₹5,000+/m' },
]

const fmt = (v) => `₹${Number(v || 0).toLocaleString('en-IN')}`

export default function DesignAssistantPage() {
  const [brief, setBrief] = useState('')
  const [garment, setGarment] = useState('')
  const [occasion, setOccasion] = useState('')
  const [season, setSeason] = useState('')
  const [budget, setBudget] = useState('')
  const [result, setResult] = useState(null)

  const { data: historyData } = useQuery({
    queryKey: ['design-history'],
    queryFn: () => designAssistantApi.history().then(r => r.data),
  })

  const generate = useMutation({
    mutationFn: (d) => designAssistantApi.generate(d),
    onSuccess: (res) => {
      setResult(res.data.data)
      toast.success(`Found ${res.data.data.products?.length || 0} matching fabrics!`)
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to generate recommendations'),
  })

  const history = historyData?.data || []

  function handleSubmit(e) {
    e.preventDefault()
    if (!brief.trim()) return toast.error('Please describe your design vision')
    generate.mutate({
      brief,
      garment_type: garment,
      occasion,
      season,
      budget_range: budget,
    })
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Wand2 className="w-6 h-6 text-pink-500" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">AI Design Assistant</h1>
          <p className="text-xs text-gray-500">Describe your vision and get smart fabric recommendations</p>
        </div>
      </div>

      {/* Input form */}
      <form onSubmit={handleSubmit} className="bg-white rounded-xl border p-6 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Describe your design *</label>
          <textarea value={brief} onChange={e => setBrief(e.target.value)}
            placeholder="e.g. I'm designing a lightweight summer saree with floral motifs for a beach wedding. Looking for something breathable with good drape..."
            rows={3}
            className="w-full border rounded-lg px-3 py-2 text-sm resize-none" />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Garment Type</label>
            <select value={garment} onChange={e => setGarment(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm">
              <option value="">Any</option>
              {GARMENT_TYPES.map(g => <option key={g} value={g.toLowerCase()}>{g}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Occasion</label>
            <select value={occasion} onChange={e => setOccasion(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm">
              <option value="">Any</option>
              {OCCASIONS.map(o => <option key={o} value={o.toLowerCase()}>{o}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Season</label>
            <select value={season} onChange={e => setSeason(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm">
              <option value="">Any</option>
              {SEASONS.map(s => <option key={s} value={s.toLowerCase()}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Budget</label>
            <select value={budget} onChange={e => setBudget(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm">
              <option value="">Any</option>
              {BUDGETS.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
            </select>
          </div>
        </div>

        <button type="submit" disabled={generate.isPending}
          className="px-6 py-2.5 rounded-lg text-sm font-bold text-white bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 transition disabled:opacity-50 flex items-center gap-2">
          {generate.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          Get Recommendations
        </button>
      </form>

      {/* Results */}
      {result && (
        <div className="space-y-6">
          {/* Suggestions */}
          {result.suggestions?.fabric_suggestions?.length > 0 && (
            <div className="bg-purple-50 rounded-xl border border-purple-200 p-5">
              <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <Lightbulb className="w-4 h-4 text-purple-500" /> Fabric Suggestions
              </h3>
              <div className="space-y-2">
                {result.suggestions.fabric_suggestions.map((s, i) => (
                  <p key={i} className="text-sm text-gray-700">{s.text}</p>
                ))}
              </div>
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-4">
            {/* Design Tips */}
            {result.suggestions?.design_tips?.length > 0 && (
              <div className="bg-white rounded-xl border p-5">
                <h3 className="font-bold text-sm text-gray-700 mb-3">Design Tips</h3>
                <ul className="space-y-2">
                  {result.suggestions.design_tips.map((tip, i) => (
                    <li key={i} className="text-xs text-gray-600 flex items-start gap-2">
                      <span className="text-pink-400 mt-0.5">•</span> {tip}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Color Palette */}
            {result.suggestions?.color_palette?.length > 0 && (
              <div className="bg-white rounded-xl border p-5">
                <h3 className="font-bold text-sm text-gray-700 mb-3 flex items-center gap-2">
                  <Palette className="w-4 h-4 text-pink-400" /> Suggested Color Palette
                </h3>
                <div className="flex flex-wrap gap-2">
                  {result.suggestions.color_palette.map(c => (
                    <span key={c} className="px-3 py-1.5 bg-gray-100 rounded-full text-xs font-medium text-gray-700">{c}</span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Matched Products */}
          {result.products?.length > 0 && (
            <div>
              <h3 className="font-bold text-gray-900 mb-3">
                Recommended Fabrics ({result.products.length})
                {result.supplier_count > 0 && (
                  <span className="text-xs text-gray-500 font-normal ml-2">from {result.supplier_count} suppliers</span>
                )}
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {result.products.map(p => (
                  <Link key={p.id} to={`/fabrics/${p.slug}`}
                    className="group bg-white rounded-xl border overflow-hidden hover:shadow-md transition-shadow">
                    <div className="aspect-square overflow-hidden bg-gray-100 relative">
                      {p.image ? (
                        <img src={p.image} alt={p.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-300">No image</div>
                      )}
                      {p.is_gi_tagged && (
                        <span className="absolute top-2 left-2 bg-green-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                          GI
                        </span>
                      )}
                    </div>
                    <div className="p-3">
                      <p className="text-xs text-gray-500">{p.fabric_type || p.category}</p>
                      <h4 className="text-sm font-bold text-gray-900 truncate">{p.name}</h4>
                      <p className="text-sm font-bold text-pink-600 mt-1">{fmt(p.price)}/m</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Care Notes */}
          {result.suggestions?.care_notes?.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <h3 className="font-bold text-sm text-amber-800 mb-2 flex items-center gap-2">
                <ShieldCheck className="w-4 h-4" /> Care Notes
              </h3>
              {result.suggestions.care_notes.map((note, i) => (
                <p key={i} className="text-xs text-amber-700">{note}</p>
              ))}
            </div>
          )}

          <p className="text-[10px] text-gray-400 text-right">
            Generated in {result.processing_ms}ms · Session: {result.session_id?.slice(0, 8)}
          </p>
        </div>
      )}

      {/* History */}
      {history.length > 0 && !result && (
        <div className="bg-white rounded-xl border p-5">
          <h3 className="font-bold text-sm text-gray-700 mb-3 flex items-center gap-2">
            <Clock className="w-4 h-4 text-gray-400" /> Recent Sessions
          </h3>
          <div className="space-y-2">
            {history.map(s => (
              <div key={s.id} className="flex items-center justify-between text-sm p-2 hover:bg-gray-50 rounded-lg">
                <div className="min-w-0 flex-1">
                  <p className="text-gray-800 truncate">{s.brief}</p>
                  <p className="text-[10px] text-gray-400">{new Date(s.created_at).toLocaleDateString()}</p>
                </div>
                <span className="text-[10px] text-gray-400 flex-shrink-0 ml-2">{s.processing_ms}ms</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
